<template>
  <div id="app">
    <div id="twitchboi">
      <Twitch/>
    </div>

    <el-container v-loading="appLoading">
      <Sidebar/>
      <el-container class="view">
        <el-header class="titlebar">
          <el-row type="flex" justify="space-between">
            <el-col>
              <el-popover
                v-if="currentPageDepth === 0"
                placement="bottom-start"
                :title="`Gold Balance: ${Number(gold).toLocaleString()}`"
                trigger="hover">
                <div slot="reference" class="gold-text">
                  <el-tag
                    type="lz-coin"
                  >
                    <span>{{ Number(gold).toLocaleString() }}</span>
                  </el-tag>
                </div>
                <div>Head on over to the "Redeem Free Likes" Page to start getting your free likes!</div>
                <div>
                  <br>
                  <el-button type="primary" @click="getGold">Refresh Gold</el-button>
                </div>
              </el-popover>
              <el-button-group v-else class="titlebar-page-button-grp">
                <el-button
                  @click="popPageDepth" 
                  class="titlebar-button">
                  <i class="lz-icon-caret"></i>
                  Back
                </el-button>
              </el-button-group>
            </el-col>
            
            <el-col>
              <el-tag
                v-if="systemStatusInfo"
                :type="systemStatusInfo.type"
              >
                {{ systemStatusInfo.description }}
              </el-tag>
              <el-button-group class="titlebar-app-button-grp">
                <el-button 
                  @click="minimizeWindow" 
                  class="titlebar-button lz-icon-minus">
                </el-button>
                <el-button 
                  @click="closeWindow" 
                  class="titlebar-button lz-icon-close" >
                </el-button>
              </el-button-group>
            </el-col>
          </el-row>
        </el-header>
        <el-main v-loading="false">

          <Tutorial v-if="!tutorialDone"/>

          <Redeem v-if="currentPage === Pages.Redeem"/>
          <GoldHistory v-if="currentPage === Pages.GoldHistory"/>
          <Settings v-if="currentPage === Pages.Settings"/>
          <Accounts v-if="currentPage === Pages.Accounts"/>
          <Orders v-if="currentPage === Pages.Orders"/>
          <AddAccount v-if="currentPage === Pages.AddAccount"/>
          <FAQ v-if="currentPage === Pages.FAQ"/>
          <News v-if="currentPage === Pages.News"/>
        </el-main>
        <el-footer>
          <div class="footer-text footer-disclaimer">
            By using this application you agree to our <a @click="openTerms">Terms & Conditions</a> found on Likezoid.com!
          </div>
          <div class="footer-text footer-build">
            {{ lzVersionString }} - Build: {{ installId }} 
          </div>
        </el-footer>
      </el-container>
    </el-container>

    <!-- Misc. components go here -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

// Functional components
import Twitch from './components/Twitch'
import Sidebar from './components/Sidebar'
import Tutorial from './components/Tutorial'

// Pages
import Redeem from './pages/Redeem'
import Settings from './pages/Settings'
import AddAccount from './pages/AddAccount'
import Accounts from './pages/Accounts'
import Orders from './pages/Orders'
import GoldHistory from './pages/GoldHistory'
import FAQ from './pages/FAQ'
import News from './pages/News'

import { BridgeAPI } from './bridge'

export default {
  name: 'app',
  components: {
    Twitch,
    Tutorial,
    Sidebar,
    Accounts,
    AddAccount,
    Orders,
    GoldHistory,
    News,
    FAQ,
    Redeem,
    Settings
  },
  async mounted () {
    this.installId = await BridgeAPI.getInstallId();
    this.lzVersion = await BridgeAPI.getLikezoidVersion();
    this.lzVersionString = `Likezoid v${this.lzVersion}`;

    await this.getGold();
    await this.loadAccounts();              // Load the accounts from config
    await this.checkAccountsLoginStatus();  // Check their login status
    this.tutorialDone = await BridgeAPI.getTutorialDone();
    // Reload all accounts every 10 seconds so we can detect if the account is having issues.
    this.accountGoldLooper = setInterval(this.getGold.bind(this), 240000);
    this.accountLoaderLooper = setInterval(this.loadAccounts.bind(this), 120000); // Reload the accounts every 10 seconds
    this.accountCheckerLooper = setInterval(this.checkAccountsLoginStatus.bind(this), 600000); // check their accounts every 10 minutes
    let needsToUpdate = false;

    try {
      const lzVersion = await BridgeAPI.getLikezoidVersion().catch(err => {
        console.error(err); // eslint-disable-line
        return '';
      });
      if (!lzVersion || !lzVersion.length) {
        needsToUpdate = true; // probably true
      }

      // https://deployment.nyc3.digitaloceanspaces.com/latest-mac.yml
      // https://deployment.nyc3.digitaloceanspaces.com/latest.yml
      const lzVersions = lzVersion.split('.').map((n) => parseInt(n));
      const major = lzVersions[0];
      const median = lzVersions[1];
      const minor = lzVersions[2];

      if (major === 1 && median === 0 && minor < 89) {
        needsToUpdate = true;
      }

      if (needsToUpdate === true) {
        const h = this.$createElement;
        this.$msgbox({
          title: 'Update Required',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          closeOnHashChange: false,
          beforeClose: (ok) => {
            if (ok === 'confirm') {
              BridgeAPI.openLink('https://likezoid.com/update-likezoid/');
            }
            return false;
          },
          message: h('div', null, [
            h('p', { style: 'text-align:left'}, [
              h('span', null, [
                'It looks like your Likezoid app is out of date.'
              ]),
              h('br', null, null),
              h('span', null, [
                'We\'ve fixed a ton of bugs recently, and are actively working on new and improved features for the app.'
              ]),
              h('br', null, null),
              h('span', null, `In order to continue using Likezoid, you must update the app. Click the button below to download the latest version from Likezoid, or visit Likezoid.com and download it manually.`),
              h('br', null, null),
              h('br', null, null),
              h('span', null, `If you still see this message, uninstall and reinstall Likezoid.`),
            ]),
          ]),
          roundButton: true,
          center: true,
          showCancelButton: false,
          showClose: false,
          confirmButtonText: `Update Likezoid`
        })
          .then(() => {
            // open likezoid.com
            BridgeAPI.openLink('https://likezoid.com/update-likezoid/');
          }).catch(() => {})
      
      }
    } catch (err) {
      console.error(err); // eslint-disable-line
    }

    // Re-identify every 10 minutes. This will also sync the accounts.
    this.reidentifyLooper = setInterval(() => {
      BridgeAPI.sendIdentify();
    }, 600000);
  },
  data () {
    return {
      addAccountDialogVisible: false,
      isCollapse: false,
      toggle: true,
      accountLoaderLooper: null,
      accountCheckerLooper: null,
      accountGoldLooper: null,
      reidentifyLooper: null,
      tutorialDone: null,
      
      installId: '',
      lzVersion: '',
      lzVersionString: '',

    }
  },
  computed: {
    env () {
      return process.env;
    },
    ...mapGetters([
      'appLoading',
      'allAccounts',
      'activeAccount',
      'currentPage',
      'currentPageDepth',
      'gold',
      'Pages',
      'systemStatusInfo'
    ]),
    likezoidVersion () {
      if (BridgeAPI && BridgeAPI.getLikezoidVersion && BridgeAPI.getLikezoidVersion() !== undefined) {
        return `Likezoid v${BridgeAPI.getLikezoidVersion()}`;
      } else {
        return 'Update Required';
      }
    },
    // installId () {
    //   return BridgeAPI.getInstallId();
    // }
  },
  methods: {
    getLikezoidVersion () {
      if (BridgeAPI && BridgeAPI.getLikezoidVersion && BridgeAPI.getLikezoidVersion() !== undefined) {
        return `Likezoid v${BridgeAPI.getLikezoidVersion()}`;
      } else {
        return 'Update Required';
      }
    },
    async minimizeWindow () {
      BridgeAPI.minimizeWindow();
    },
    async closeWindow () {
      BridgeAPI.closeWindow();
    },
    async checkAccountsLoginStatus () {
      this.allAccounts.forEach(async account => {
        if (!account.needsLogin || !account.likeBlocked) {
          await BridgeAPI.fetchEditProfile(account.profile.username);
        }
      })
    },
    async openTerms () {
      const url = process.env.VUE_APP_SITE_TOS;
      BridgeAPI.openLink(url);
    },
    async getGold () {
      await this.fetchGold().catch(() => {
        const h = this.$createElement;
        this.$msgbox({
          title: 'Error fetching gold',
          message: h('div', null, [
            h('p', { style: 'text-align:left'}, [
              h('span', null, [
                'We could not fetch your gold - the server might be down for maintenance.'
              ]),
              h('br', null, null),
              h('br', null, null),
              h('span', null, 'Feel free to try to refresh it manually by clicking the button below!'),
              h('br', null, null),
              h('br', null, null),
            ]),
          ]),
          roundButton: true,
          center: true,
          showCancelButton: true,
          confirmButtonText: 'Try fetching Gold again',
          cancelButtonText: 'Cancel'
        }).then(() => {
          this.getGold();
        }).catch(() => {});
      });
    },
    ...mapActions({
      fetchGold: 'fetchGold',
      loadAccounts: 'loadAccounts',
      navigate: 'changePage',
      popPageDepth: 'popPageDepth'
    })
  }
}
</script>

<style lang="scss">
#app {
  margin: 0px;
  padding: 0px;

  .view {
    position: relative;
    background-color: #fff;
    background-image: url('./assets/img/view/background.svg');
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;

    .titlebar {
      background: #ffffff;
      padding: 22px 43px 22px 26px;
      height: auto !important;
      border-bottom: solid 1px #e6e6e6;

      .el-col {
        width: auto;

        > *:not(:first-child) {
          margin-left: 15px;
        }
      }

      .el-button-group {
        box-shadow: 0px 1px 3px rgba(50, 59, 91, 0.12);

        .el-button {
          padding: 9px 7px;
          min-width: 27px;
          height: 31px;
          color: #777784;
          font-size: 12px;
          font-weight: 600;
          border: none;

          i {
            margin-right: 16px;
          }

          &:active {
            box-shadow: 0 1px 3px inset rgba(50, 59, 91, 0.12);
          }

          &:active,
          &:hover,
          &:focus {
            background: #f2f1f3;
          }

          &:not(:last-child) {
            margin: 0;
            border-right: 1px solid #e7e7ed;
          }
        }

        &.titlebar-gold-button-grp .el-button {
          color: #e88473;
        }
      }
    }

    .el-main {
      padding: 58px 115px;
      height: calc(100vh - 155px);
    }
  }

  .bg-purple {
    background: #d3dce6;
  }

  .dropdown-username-card {
    cursor: pointer;
  }

  .titlebar-button {
    -webkit-app-region: no-drag;
  }

  .dropdown-add-username-card {
    cursor: pointer;
  }

  .gold-text {
    font-size: 16px;
    font-weight: bold;
    
    i {
      color: #f0e111;
    }
  }

  .centered-text {
    text-align: center;
  }

  .el-footer {
    position: relative;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    height: auto !important;
    z-index: 100;

    .footer-text {
      padding: 11px 21px;
      color: #9597ae;
      font-size: 11px;
      background: #FFFFFF;
      box-shadow: 0px 1px 3px rgba(50, 59, 91, 0.12);
      border-radius: 16.5px;

      a, a:active, a:hover {
        color: #e88473;
      }
    }
  }
}
</style>
